import React, { useState } from 'react';
import goldColors from '../themes/goldColors';
import LanguageSwitcherV2 from './LanguageSwitcherV2';
import PaymentMethodsBadges from './PaymentMethodsBadges';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  CardMedia, 
  Divider, 
  Tabs, 
  Tab, 
  TextField, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Chip,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useLanguage } from '../contexts/LanguageContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PieChartIcon from '@mui/icons-material/PieChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlanIcon from '@mui/icons-material/CalendarMonth';
import ControlIcon from '@mui/icons-material/Settings';
import MonitorIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PaidIcon from '@mui/icons-material/Paid';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedIcon from '@mui/icons-material/Verified';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import Groups2Icon from '@mui/icons-material/Groups2';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AppShowcase, { PcmFeaturesGrid } from './AppShowcase';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(10, 2),
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url(/images/pattern.svg) repeat',
    opacity: 0.1,
    zIndex: 1,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(4),
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: -10,
    height: 4,
    width: 60,
    backgroundColor: theme.palette.primary.main,
  }
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  }
}));

const PricingCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'featured'
})(({ theme, featured }) => ({
  height: '100%',
  transition: 'transform 0.3s ease',
  border: featured ? `2px solid ${theme.palette.primary.main}` : 'none',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  }
}));

const HomePage = () => {
  const [tabValue, setTabValue] = useState(0);
  // 'personal' | 'business' — mismo toggle que existe en /precios, para que
  // un visitante que entra directo a la home también vea que hay un plan
  // pensado para dueños de pequeños negocios (la clientela real de la
  // consultoría), no solo para uso personal.
  // Formulario "Envíanos un Mensaje" — antes no tenía onSubmit ni estado,
  // así que el botón no hacía nada. Arreglo rápido sin backend: arma un
  // mailto: con lo que escribió el visitante y abre su cliente de correo
  // ya con el mensaje listo para enviar a augusto@darwinoptima.com.
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleContactFormSubmit = (e) => {
    e.preventDefault();
    const subject = language === 'es' ? 'Contacto desde PCM Finanzas' : 'Contact from PCM Finance';
    const body =
      `${language === 'es' ? 'Nombre' : 'Name'}: ${contactForm.name}\n` +
      `${language === 'es' ? 'Correo' : 'Email'}: ${contactForm.email}\n\n` +
      `${contactForm.message}`;
    window.location.href = `mailto:augusto@darwinoptima.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, language } = useLanguage();
  // 10 ago 2026 (Augusto: "cuando me fui a la zona de pagos, y regresé a
  // home, me sale que estoy logueado, pero igual me sale la opcion de
  // crear cuenta o accesar cuenta"): HomePage.js nunca miraba el estado de
  // sesión (a diferencia de PublicHeader.js, que sí usa este mismo
  // patrón), así que los botones de "Crear Cuenta"/"Portal de Clientes"
  // salían siempre, incluso con sesión iniciada.
  const { token } = useSelector((state) => state.auth);
  

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Theme is already defined via useTheme above

  // PCM Steps data
  const pcmSteps = [
    {
      title: language === 'es' ? "Planificación" : "Planning",
      icon: <PlanIcon fontSize="large" sx={{ color: goldColors.main }} />,
      description: language === 'es' ? "Establezca objetivos financieros claros, cree presupuestos y establezca un plan para alcanzar sus objetivos financieros." : "Set clear financial goals, create budgets, and establish a roadmap to achieve your financial objectives.",
      features: language === 'es' ? ["Definición de objetivos", "Creación de presupuestos", "Planificación financiera"] : ["Goal setting", "Budget creation", "Financial roadmapping"]
    },
    {
      title: language === 'es' ? "Control" : "Controlling", 
      icon: <ControlIcon fontSize="large" sx={{ color: goldColors.main }} />,
      description: language === 'es' ? "Tome el control de su futuro financiero con herramientas intuitivas para gestionar gastos, ahorros e inversiones." : "Take command of your financial future with intuitive tools to manage spending, savings, and investments.",
      features: language === 'es' ? ["Gestión de gastos", "Reducción de deudas", "Automatización de ahorros"] : ["Expense management", "Debt reduction", "Saving automation"]
    },
    {
      title: language === 'es' ? "Monitoreo" : "Monitoring",
      icon: <MonitorIcon fontSize="large" sx={{ color: goldColors.main }} />,
      description: language === 'es' ? "Realice un seguimiento de su progreso en tiempo real con análisis completos, informes y alertas." : "Track your progress in real-time with comprehensive analytics, reports and alerts.",
      features: language === 'es' ? ["Seguimiento en tiempo real", "Paneles personalizables", "Alertas de gastos"] : ["Real-time tracking", "Customizable dashboards", "Spending alerts"]
    }
  ];

  // Pricing plans data — 2 planes del aplicativo (la consultoría tiene su
  // propio precio, por separado, en /precios). Personal cubre lo que ya
  // existe hoy; Pro incluye lo que se va construyendo (IA, canales
  // múltiples) a medida que esas funciones queden listas.
  const pricingPlans = [
    {
      title: language === 'es' ? "Personal" : "Personal",
      price: "$9.99",
      period: language === 'es' ? "por mes" : "per month",
      features: language === 'es' ? [
        "Asistente de configuración inicial",
        "Ingreso manual de transacciones",
        "Calendario de flujos de caja",
        "Categorización de ingresos y egresos",
        "Herramientas de gestión de deudas",
        "Informes básicos",
        // 4 ago 2026: diferenciadores gratis (sin IA) pedidos por Augusto
        // -- ver bulk_import.py y admin.py (debt-reminders/send) en el
        // backend. 17 sep 2026: "Duplicar el mes anterior" se quitó de la
        // oferta (el endpoint existe pero nunca tuvo botón en la app).
        "Importador de CSV/Excel sin costo adicional",
        "Recordatorios de vencimiento de deudas por correo (opcional)"
      ] : [
        "Initial setup assistant",
        "Manual transaction entry",
        "Cash flow calendar",
        "Income and expense categorization",
        "Debt management tools",
        "Basic reports",
        "Free CSV/Excel importer",
        "Debt due-date email reminders (optional)"
      ],
      featured: false,
      buttonText: language === 'es' ? "Prueba 14 días gratis" : "Try 14 Days Free",
      buttonVariant: "outlined"
    },
    {
      title: language === 'es' ? "Pro" : "Pro",
      // 30 ago 2026 (pedido de Augusto: "aun no está a la venta así que
      // podemos subir precios sin que nadie se queje"): mismo precio que
      // PreciosPage.js -- ver PLAN_PRICE_PRO_USD en
      // backend_new/app/core/config.py.
      price: "$29.00",
      period: language === 'es' ? "por mes" : "per month",
      features: language === 'es' ? [
        "Todo lo de Personal",
        // 4 ago 2026 (pedido de Augusto): el importador de estados de
        // cuenta se fusiona dentro de esta misma línea, justo después de
        // "ingreso multi canal" -- antes vivía como bullet aparte.
        "Ingreso de transacciones multi canal (WhatsApp, voz) e importador de estados de cuenta con IA",
        "Auto-categorización y alertas con IA",
        "Reportes inteligentes con IA",
        "Pregúntale a la IA: preguntas libres sobre tus finanzas, con gráficos",
        "Constructor de presupuesto con IA: te sugiere cuánto gastar por categoría y qué recortar",
        // 4 ago 2026: PLAN_PAGE_QUOTA['pro'] = 150 en
        // backend_new/app/core/plans.py.
        "150 páginas de estados de cuenta al mes (puedes comprar más si necesitas)",
        "Soporte por correo electrónico"
      ] : [
        "Everything in Personal",
        "Multi-channel transaction entry (WhatsApp, voice) and AI statement importer",
        "AI auto-categorization and alerts",
        "AI-powered smart reports",
        "Ask the AI: free-form questions about your finances, with charts",
        "AI budget builder: suggests how much to spend per category and what to cut",
        "150 statement pages per month (buy more if you need them)",
        "Email support"
      ],
      featured: true,
      buttonText: language === 'es' ? "Prueba 14 días gratis" : "Try 14 Days Free",
      buttonVariant: "contained"
    }
  ];

  // 17 sep 2026 (Augusto: "Elimina plan negocios. Eso lo manejamos con
  // Capital Optima"): los planes Negocio Básico / Negocio Pro salieron de
  // PCM. La oferta para empresas vive en CapitalOptima
  // (capo.darwinoptima.com), ver Notion "CapitalOptima — Caja, Deuda y
  // Estados Financieros Reales". PCM queda solo para personas.
  const activePricingPlans = pricingPlans;

  return (
    <Box>
      {/* Header with language switcher */}
      {/* El selector de idioma se ha eliminado de aquí para evitar duplicaciones.
         Solo se mantiene el selector en PublicHeader. */}

      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" gutterBottom>
                {t('appName', 'common')}
              </Typography>
              <Typography variant="h5" color="textSecondary" paragraph>
                {t('planningDescription', 'common')}
              </Typography>
              
              {/* Added new emphasis on app features */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" color="white" fontWeight="medium" gutterBottom>
                  {language === 'es' ? 'Una plataforma financiera diseñada para ti:' : 'A financial platform designed for you:'}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TouchAppIcon sx={{ mr: 1, color: goldColors.light }} />
                      <Typography variant="body2" color="white">
                        {language === 'es' ? 'Intuitiva y fácil de usar' : 'Intuitive & easy to use'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PsychologyIcon sx={{ mr: 1, color: goldColors.light }} />
                      <Typography variant="body2" color="white">
                        {language === 'es' ? 'Asistida por IA' : 'AI-assisted'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SupportAgentIcon sx={{ mr: 1, color: goldColors.light }} />
                      <Typography variant="body2" color="white">
                        {language === 'es' ? 'Expertos financieros' : 'Finance professionals'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MergeTypeIcon sx={{ mr: 1, color: goldColors.light }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" color="white" sx={{ lineHeight: 1.2 }}>
                          {language === 'es' ? 'Ingreso' : 'Input'}
                        </Typography>
                        <Typography variant="body2" color="white" sx={{ lineHeight: 1.2 }}>
                          {language === 'es' ? 'Multi-Canal' : 'Multi-Channel'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {token ? (
                  <Button
                    variant="contained"
                    size="large"
                    component={RouterLink}
                    to="/dashboard"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ py: 1.5, px: 3 }}
                  >
                    {language === 'es' ? 'Ir a mi Panel' : 'Go to my Dashboard'}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      component={RouterLink}
                      to="/register"
                      endIcon={<ArrowForwardIcon />}
                      sx={{ py: 1.5, px: 3 }}
                    >
                      {language === 'es' ? 'Crear Cuenta' : 'Create Account'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      color="inherit"
                      component={RouterLink}
                      to="/login?from=portal"
                      sx={{ py: 1.5, px: 3 }}
                    >
                      {language === 'es' ? 'Portal de Clientes' : 'Client Portal'}
                    </Button>
                  </>
                )}
              </Box>
              <Box sx={{ mt: 7, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Chip 
                  icon={<VerifiedIcon sx={{ fontSize: '0.7rem' }} />} 
                  label={language === 'es' ? 'Plataforma Segura' : 'Secure Platform'} 
                  variant="outlined" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white', 
                    border: 'none', 
                    fontSize: '0.7rem', 
                    height: 20, 
                    '& .MuiChip-label': { px: 1, py: 0 },
                    '& .MuiChip-icon': { ml: 0.5, mr: 0.3 }
                  }}
                  size="small"
                />
                <Chip 
                  icon={<VerifiedIcon sx={{ fontSize: '0.7rem' }} />} 
                  label={language === 'es' ? 'Ayuda con IA 24/7' : '24/7 AI help'} 
                  variant="outlined"
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white', 
                    border: 'none', 
                    fontSize: '0.7rem', 
                    height: 20,
                    '& .MuiChip-label': { px: 1, py: 0 },
                    '& .MuiChip-icon': { ml: 0.5, mr: 0.3 }
                  }}
                  size="small"
                />
              </Box>
            </Grid>
          <Grid item xs={12} md={6}>
            <AppShowcase language={language} />
          </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Todo lo que hace PCM hoy -- oct 2026. Mismo orden que el menú de la app. */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#FAFAFA' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" align="center" fontWeight="bold" gutterBottom>
            {language === 'es' ? 'Todo lo que hace PCM hoy' : 'Everything PCM does today'}
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4, maxWidth: 720, mx: 'auto' }}>
            {language === 'es' ? 'Organizado igual que el menú de la app: cómo vas, tu dinero, tus movimientos, tu plan, la IA y la asesoría.' : 'Organized just like the app menu: how you are doing, your money, your activity, your plan, AI and human advice.'}
          </Typography>
          <PcmFeaturesGrid language={language} />
        </Container>
      </Box>

      {/* El convertidor de divisas se eliminó de aquí porque ya existe uno en el menú superior */}

      {/* PCM Framework Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {language === 'es' ? 'NUESTRO ENFOQUE' : 'OUR APPROACH'}
          </Typography>
          <Typography variant="h3" gutterBottom fontWeight="bold">
            {language === 'es' ? 'La Metodología PCM' : 'The PCM Framework'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            {language === 'es' ? 'Le guiamos a través de los tres pilares esenciales de la salud financiera: Planificación, Control y Monitoreo.' : 'We guide you through the three essential pillars of financial health: Planning, Controlling, and Monitoring.'}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {pcmSteps.map((step, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeatureCard elevation={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {step.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    {step.title}
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    {step.description}
                  </Typography>
                  <List dense>
                    {step.features.map((feature, i) => (
                      <ListItem key={i} disableGutters sx={{ justifyContent: 'center' }}>
                        <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Interactive Features Section */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {language === 'es' ? 'CARACTERÍSTICAS INTERACTIVAS' : 'INTERACTIVE FEATURES'}
            </Typography>
            <Typography variant="h3" gutterBottom fontWeight="bold">
              {language === 'es' ? 'Herramientas que te Guían hacia Adelante' : 'Tools That Guide You Forward'}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              {language === 'es' ? 'Nuestra aplicación no solo te muestra datos—los interpreta para ti y sugiere pasos accionables para mejorar.' : 'Our application doesn\'t just show you data—it interprets it for you and suggests actionable steps to improve.'}
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              centered
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{ mb: 4 }}
            >
              <Tab label={language === 'es' ? "Análisis de Gastos" : "Expense Analysis"} icon={<PieChartIcon sx={{ color: goldColors.main }} />} iconPosition="start" />
              <Tab label={language === 'es' ? "Ingresos vs. Gastos" : "Income vs. Expenses"} icon={<BarChartIcon sx={{ color: goldColors.main }} />} iconPosition="start" />
              {/* 9 sep 2026 (Augusto: "las vistas... no son iguales a las
                  que tenemos en el aplicativo"): "Proyección de Ahorros"
                  prometía un algoritmo de proyección/escenarios que no
                  existe -- la función real es "Tendencia de ahorro y
                  deuda" (SavingsDebtTrend.js / /reports/trend), un
                  historial de los últimos meses, no una proyección a
                  futuro. */}
              <Tab label={language === 'es' ? "Tendencia de Ahorro" : "Savings Trend"} icon={<TimelineIcon sx={{ color: goldColors.main }} />} iconPosition="start" />
            </Tabs>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    width: '100%', 
                    borderRadius: 2, 
                    p: 3,
                    height: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                  }}
                >
                  {/* Título del gráfico */}
                  <Box sx={{ position: 'absolute', top: 16, left: 16 }}>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {tabValue === 0 && (language === 'es' ? "Gastos Mensuales" : "Monthly Expenses")}
                      {tabValue === 1 && (language === 'es' ? "Ingresos vs. Gastos" : "Income vs. Expenses")}
                      {tabValue === 2 && (language === 'es' ? "Tendencia de Ahorro" : "Savings Trend")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tabValue === 0 && (language === 'es' ? "Últimos 30 días" : "Last 30 days")}
                      {tabValue === 1 && (language === 'es' ? "Últimos 4 meses" : "Last 4 months")}
                      {tabValue === 2 && (language === 'es' ? "Últimos 6 meses" : "Last 6 months")}
                    </Typography>
                  </Box>

                  {/* Contenido del gráfico */}
                  {tabValue === 0 && (
                    /* Gráfico circular de gastos */
                    <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', mt: 3 }}>
                      <Box sx={{ position: 'relative', width: 220, height: 220 }}>
                        {/* Segmentos del gráfico de dona */}
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'conic-gradient(#3f51b5 0deg 110deg, #f44336 110deg 190deg, #4caf50 190deg 240deg, #ff9800 240deg 300deg, #9c27b0 300deg 360deg)',
                          borderRadius: '50%',
                        }} />
                        {/* Center hole */}
                        <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '60%',
                          height: '60%',
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="h6" fontWeight="bold">$2,254</Typography>
                          <Typography variant="caption">{language === 'es' ? 'Total gastado' : 'Total Spent'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ ml: 3 }}>
                        {[
                          { color: '#3f51b5', label: language === 'es' ? 'Vivienda' : 'Housing', value: '32%' },
                          { color: '#f44336', label: language === 'es' ? 'Alimentación' : 'Food', value: '22%' },
                          { color: '#4caf50', label: language === 'es' ? 'Transporte' : 'Transport', value: '15%' },
                          { color: '#ff9800', label: language === 'es' ? 'Servicios' : 'Utilities', value: '18%' },
                          { color: '#9c27b0', label: language === 'es' ? 'Otros' : 'Others', value: '13%' },
                        ].map((item, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: item.color, mr: 1 }} />
                            <Typography variant="body2" sx={{ mr: 1 }}>{item.label}</Typography>
                            <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {tabValue === 1 && (
                    /* 9 sep 2026 (Augusto: "las vistas... no son iguales a
                       las que tenemos en el aplicativo"): esto simulaba
                       "ingresos por tipo" (Salario/Freelance/Inversiones/
                       Otros) con categorización automática -- esa función
                       no existe. Lo real es el Reporte Mensual (Ingresos
                       vs. Gastos, con el % de variación vs. el mes
                       anterior que se agregó el 7 sep 2026), así que el
                       mockup pasa a mostrar eso: 2 barras por mes, en
                       soles. */
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, mt: 3, position: 'relative' }}>
                      <Box sx={{ zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, mx: 3 }}>
                        <Typography variant="subtitle1" fontWeight="medium" color="text.primary" sx={{ mb: 1 }}>
                          {language === 'es' ? 'Ingresos vs. Gastos por mes' : 'Income vs. Expenses by month'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                          {[
                            { color: 'success.main', label: language === 'es' ? 'Ingresos' : 'Income' },
                            { color: 'error.main', label: language === 'es' ? 'Gastos' : 'Expenses' },
                          ].map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: item.color }} />
                              <Typography variant="caption">{item.label}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', flex: 1, mx: 3, justifyContent: 'space-around', alignItems: 'flex-end', height: 150 }}>
                        {[
                          { month: language === 'es' ? 'Ene' : 'Jan', ingresos: 2200, gastos: 1600 },
                          { month: language === 'es' ? 'Feb' : 'Feb', ingresos: 2200, gastos: 1750 },
                          { month: language === 'es' ? 'Mar' : 'Mar', ingresos: 2450, gastos: 1900 },
                          { month: language === 'es' ? 'Abr' : 'Apr', ingresos: 2450, gastos: 1580 },
                        ].map((m, i) => (
                          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 70 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: 110 }}>
                              <Box sx={{ width: 16, height: `${(m.ingresos / 2500) * 100}%`, bgcolor: 'success.main', borderRadius: '3px 3px 0 0' }} />
                              <Box sx={{ width: 16, height: `${(m.gastos / 2500) * 100}%`, bgcolor: 'error.main', borderRadius: '3px 3px 0 0' }} />
                            </Box>
                            <Typography variant="caption" fontWeight="medium" sx={{ mt: 0.5 }}>{m.month}</Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Comparativa mes a mes -- misma pieza que se agregó
                          al Reporte Mensual real el 7 sep 2026
                          (renderVariacionMensual en Reports.js). */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2.5, mb: 1 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {language === 'es' ? 'Ingresos (abril)' : 'Income (April)'}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="success.main">$2,450</Typography>
                          <Typography variant="caption" color="success.main" fontWeight="bold">
                            {language === 'es' ? '+0% vs. mes pasado' : '+0% vs. last month'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {language === 'es' ? 'Gastos (abril)' : 'Expenses (April)'}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="error.main">$1,580</Typography>
                          <Typography variant="caption" color="success.main" fontWeight="bold">
                            {language === 'es' ? '-17% vs. mes pasado' : '-17% vs. last month'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {tabValue === 2 && (
                    /* 9 sep 2026 (Augusto: "las vistas... no son iguales a
                       las que tenemos en el aplicativo"): esto simulaba un
                       comparador "plan actual vs. plan optimizado" con
                       "algoritmos inteligentes" que proyectan un 36% más
                       de ahorro -- nada de eso existe. Lo real es
                       SavingsDebtTrend.js / GET /reports/trend: un
                       historial (no una proyección) del ahorro neto de los
                       últimos meses, positivo o negativo según el mes. */
                    <Box sx={{
                      mt: 2,
                      mb: 2,
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      overflow: 'hidden',
                      p: 2,
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, bgcolor: 'success.main', borderRadius: '2px' }} />
                          <Typography variant="caption" color="text.secondary">
                            {language === 'es' ? 'Ahorro neto (ingresos - gastos)' : 'Net savings (income - expenses)'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: 130 }}>
                        {[
                          { month: language === 'es' ? 'Nov' : 'Nov', ahorro: 420 },
                          { month: language === 'es' ? 'Dic' : 'Dec', ahorro: -180 },
                          { month: language === 'es' ? 'Ene' : 'Jan', ahorro: 600 },
                          { month: language === 'es' ? 'Feb' : 'Feb', ahorro: 450 },
                          { month: language === 'es' ? 'Mar' : 'Mar', ahorro: 550 },
                          { month: language === 'es' ? 'Abr' : 'Apr', ahorro: 870 },
                        ].map((m, i) => (
                          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 50 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: 90 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold', color: m.ahorro >= 0 ? 'success.main' : 'error.main', mb: 0.2 }}>
                                {m.ahorro >= 0 ? '+' : ''}${m.ahorro}
                              </Typography>
                              <Box sx={{
                                width: 22,
                                height: `${Math.min(Math.abs(m.ahorro) / 900 * 100, 100)}%`,
                                bgcolor: m.ahorro >= 0 ? 'success.main' : 'error.main',
                                borderRadius: '3px 3px 0 0',
                              }} />
                            </Box>
                            <Typography variant="caption" fontWeight="medium" sx={{ mt: 0.5 }}>{m.month}</Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box sx={{
                        bgcolor: 'rgba(46, 125, 50, 0.08)',
                        borderTop: '1px solid rgba(46, 125, 50, 0.15)',
                        mt: 2,
                        p: 1.5,
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                        <Typography variant="body2" color="success.dark" fontWeight="medium" align="center">
                          {language === 'es'
                            ? 'Historial real de los últimos 6 meses -- sin proyecciones ni algoritmos, solo tus números'
                            : 'Real history of the last 6 months -- no projections or algorithms, just your numbers'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  {tabValue === 0 && (language === 'es' ? "Visualiza a dónde va tu dinero" : "Visualize Where Your Money Goes")}
                  {tabValue === 1 && (language === 'es' ? "Compara ingresos y gastos cada mes" : "Compare Income and Expenses Every Month")}
                  {tabValue === 2 && (language === 'es' ? "Sigue tu ahorro mes a mes" : "Track Your Savings Month by Month")}
                </Typography>
                <Typography paragraph color="text.secondary">
                  {tabValue === 0 && (language === 'es' ? "Los gráficos interactivos desglosan tus gastos por categoría, establecimiento comercial y período de tiempo, ayudándote a identificar áreas para optimizar." : "Interactive charts break down your spending by category, merchant, and time period, helping you identify areas to optimize.")}
                  {tabValue === 1 && (language === 'es' ? "El Reporte Mensual muestra tus ingresos y gastos lado a lado, con el porcentaje de variación contra el mes anterior para que veas de un vistazo si vas mejor o peor." : "The Monthly Report shows your income and expenses side by side, with the percentage change vs. the previous month so you see at a glance whether you're doing better or worse.")}
                  {tabValue === 2 && (language === 'es' ? "La Tendencia de Ahorro conecta tu historial real de los últimos meses -- cuánto ahorraste (o gastaste de más) mes a mes, sin proyecciones inventadas." : "The Savings Trend connects your real history from the last few months -- how much you saved (or overspent) month by month, with no invented projections.")}
                </Typography>
                <List>
                  {[
                    tabValue === 0 && (language === 'es' ? "Profundiza en las categorías de gastos" : "Drill down into spending categories"),
                    tabValue === 0 && (language === 'es' ? "Identifica tendencias y patrones a lo largo del tiempo" : "Identify trends and patterns over time"),
                    tabValue === 0 && (language === 'es' ? "Recibe sugerencias personalizadas para reducir gastos" : "Get personalized suggestions to reduce expenses"),
                    tabValue === 1 && (language === 'es' ? "Ve ingresos y gastos del mes, uno junto al otro" : "See this month's income and expenses side by side"),
                    tabValue === 1 && (language === 'es' ? "Compara contra el mes anterior en porcentaje" : "Compare against last month as a percentage"),
                    tabValue === 1 && (language === 'es' ? "Filtra por cualquier mes y año de tu historial" : "Filter by any month and year in your history"),
                    tabValue === 2 && (language === 'es' ? "Ve el ahorro neto de los últimos 6 meses" : "See your net savings from the last 6 months"),
                    tabValue === 2 && (language === 'es' ? "Detecta rápido los meses en rojo (gastaste más de lo que ganaste)" : "Quickly spot months in the red (you spent more than you earned)"),
                    tabValue === 2 && (language === 'es' ? "Incluye también cómo se movió tu deuda total en el mismo período" : "Also includes how your total debt moved over the same period")
                  ].filter(Boolean).map((item, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {language === 'es' ? 'PLANES DE PRECIOS' : 'PRICING PLANS'}
          </Typography>
          <Typography variant="h3" gutterBottom fontWeight="bold">
            {language === 'es' ? 'Elige el Plan Adecuado para Ti' : 'Choose the Right Plan for You'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            {language === 'es' ? 'Elige entre el plan Personal o Pro, diseñados para adaptarse a diferentes necesidades y objetivos financieros.' : 'Choose between our Personal or Pro plan, designed to adapt to different financial needs and goals.'}
          </Typography>

        </Box>

        <Grid container spacing={4} justifyContent="center">
          {activePricingPlans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={5} key={index}>
              <PricingCard featured={plan.featured} raised={plan.featured}>
                <CardContent sx={{ p: 3 }}>
                  {plan.featured && (
                    <Chip 
                      label={language === 'es' ? "MÁS POPULAR" : "MOST POPULAR"} 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 2 }}
                    />
                  )}
                  <Typography variant="h5" component="div" gutterBottom fontWeight="bold">
                    {plan.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                    <Typography variant="h3" component="span" fontWeight="bold">
                      {plan.price}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
                      {plan.period}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <List dense sx={{ mb: 2 }}>
                    {plan.features.map((feature, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    fullWidth
                    variant={plan.buttonVariant}
                    color="primary"
                    size="large"
                    component={RouterLink}
                    to="/register"
                    sx={{ mt: 2 }}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </PricingCard>
            </Grid>
          ))}
        </Grid>

        <PaymentMethodsBadges
          label={language === 'es' ? 'Aceptamos' : 'We accept'}
          sx={{ mt: 5 }}
        />
      </Container>


      {/* Contact Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <SectionTitle variant="h3" fontWeight="bold">
              {language === 'es' ? 'Contáctanos' : 'Contact Us'}
            </SectionTitle>
            <Typography paragraph color="text.secondary">
              {language === 'es' ? '¿Tienes preguntas o necesitas ayuda? Nuestro equipo está aquí para ayudarte en tu viaje financiero.' : 'Have questions or need assistance? Our team is here to help you on your financial journey.'}
            </Typography>
            
            <List>
              <ListItem disableGutters>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? "Correo electrónico" : "Email"}
                  secondary="augusto@darwinoptima.com"
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {language === 'es' ? 'Envíanos un Mensaje' : 'Send Us a Message'}
              </Typography>
              <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={handleContactFormSubmit}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label={language === 'es' ? "Tu Nombre" : "Your Name"}
                  name="name"
                  autoComplete="name"
                  value={contactForm.name}
                  onChange={handleContactFormChange}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={language === 'es' ? "Correo Electrónico" : "Email Address"}
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={contactForm.email}
                  onChange={handleContactFormChange}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="message"
                  label={language === 'es' ? "Tu Mensaje" : "Your Message"}
                  id="message"
                  multiline
                  rows={4}
                  value={contactForm.message}
                  onChange={handleContactFormChange}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  {language === 'es' ? 'Enviar Mensaje' : 'Send Message'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {language === 'es' ? '¿Listo para Transformar tu Vida Financiera?' : 'Ready to Transform Your Financial Life?'}
          </Typography>
          <Typography variant="subtitle1" paragraph sx={{ mb: 4 }}>
            {language === 'es' ? 'Ordena tus finanzas de una vez y quítate el dolor de cabeza de no saber en qué se va tu dinero.' : "Get your finances in order once and for all, and stop wondering where your money goes."}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={RouterLink}
            to={token ? '/dashboard' : '/login?from=portal'}
            sx={{ px: 4, py: 1.5 }}
          >
            {token
              ? (language === 'es' ? 'Ir a mi Panel' : 'Go to my Dashboard')
              : (language === 'es' ? 'Acceder al Portal de Clientes' : 'Access Client Portal')}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
